// "Cosa manca" nei moduli di richiesta (#860).
//
// The submit buttons stay clickable: a click on an incomplete form names the
// first missing thing next to its field, highlights the field and scrolls to
// it, and nothing is sent. The rules stay those of each form; this module only
// holds the shared wording and the reveal.
import { RENOVATION_PARTNER } from "@/consent/shareConsent.js";

// "Newarc S.r.l." already ends the sentence.
function withFinalDot(text) {
  return text.endsWith(".") ? text : `${text}.`;
}

export const ISSUE_MESSAGES = Object.freeze({
  daysAndSlots: "Seleziona almeno un giorno e una fascia oraria.",
  days: "Seleziona almeno un giorno.",
  slots: "Seleziona almeno una fascia oraria.",
  phone: "Inserisci il numero di telefono.",
  name: "Inserisci il tuo nome.",
  email: "Inserisci un'email valida.",
  link: "Inserisci un link valido.",
  privacy: "Accetta l'informativa privacy per continuare.",
  share: `Per continuare accetta la condivisione dei dati con ${withFinalDot(RENOVATION_PARTNER.name)}`,
});

// An issue: the field it belongs to (the `data-issue-field` of its wrapper)
// and the message shown there.
export function formIssue(field, message = ISSUE_MESSAGES[field]) {
  return { field, message };
}

// Options-API glue shared by the forms. The form defines the computed
// `pendingIssue` (its first missing thing, or null, by its own rules) and
// calls `blockOnIssue()` at the top of its submit: true means something is
// missing, now shown, and the submit must stop.
export const formIssueMixin = {
  data() {
    return { issue: null };
  },
  watch: {
    // The message goes away as soon as its field is fixed, and follows it
    // while the same field misses something else ("un giorno e una fascia"
    // -> "un giorno").
    pendingIssue(next) {
      if (this.issue && (!next || next.field !== this.issue.field)) this.issue = null;
      else if (this.issue && next && next.message !== this.issue.message) this.issue = next;
    },
  },
  methods: {
    issueFor(field) {
      return this.issue && this.issue.field === field ? this.issue.message : "";
    },
    issueClass(field) {
      return { "ic-form-issue-target": !!this.issueFor(field) };
    },
    blockOnIssue() {
      const issue = this.pendingIssue;
      if (!issue) {
        this.issue = null;
        return false;
      }
      this.issue = issue;
      this.$nextTick(() => {
        const root = this.$el && typeof this.$el.querySelector === "function" ? this.$el : document;
        revealFormIssue(root, issue.field);
      });
      return true;
    },
  },
};

// Scrolls to the wrapper of `field` inside `root` and moves the focus to its
// first control, so keyboard and screen-reader users land on it too.
export function revealFormIssue(root, field) {
  if (!root || typeof root.querySelector !== "function" || !field) return null;
  const target = root.querySelector(`[data-issue-field="${field}"]`);
  if (!target) return null;
  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  const control = target.matches("input, textarea, select, button")
    ? target
    : target.querySelector("input, textarea, select, button");
  if (control && typeof control.focus === "function") {
    try {
      control.focus({ preventScroll: true });
    } catch (e) {
      control.focus();
    }
  }
  return target;
}
